import type { PartReplacementPayload, UndoPartReplacmentPayload } from '@/types/rc/miners/PartService'
import { replacePart, undoPartReplacement } from '@/services/rc/spare-parts.service'
import { useMutation, useQueryClient } from '@tanstack/vue-query'
import type { IMinerPartAllocationForm, IMinerPartDeleteForm, IMinerPartMoveToInventoryForm } from '@/types/rc/forms'
import {
  allocatePartToMiner,
  deleteMinerPart,
  movePartToInventory,
  movePartBackFromInventory,
} from '@/services/rc/miners.service'
import type { Ref } from 'vue'
import { NotifyFail, NotifySuccess } from '@/helpers/core/notifications'

/**
 * Composable to generate part replacement mutations for a miner given the miner ID.
 *
 * @param minerId miner ID to use for encapsulated mutations.
 * @returns part replacement mutation, undo part replacement mutation targeting miner with miner ID.
 */
export const useMinerSparePartReplacements = (minerId: Ref<number>) => {
  const queryClient = useQueryClient()

  /**
   * Composable to generate an undoPartReplacement mutation for the factory's minerId.

   * @returns spare part replacement mutation.
   */
  const usePartReplacementMutation = () =>
    useMutation({
      mutationKey: ['spare-part-replacement', minerId, 'replacement'],
      mutationFn: (payload: PartReplacementPayload) => replacePart(minerId.value, payload),
      onError: (e) => {
        NotifyFail(e.message)
      },
      onSuccess: async () => {
        NotifySuccess('Successfully replaced part.')
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['miner-details', minerId] }),
          queryClient.invalidateQueries({ queryKey: ['spare-parts'] }),
        ])
      },
    })

  /**
   * Composable to generate an undoPartReplacement mutation for the factory's minerId.
   *
   * @returns undo spare part replacement mutation.
   */
  const useUndoPartReplacementMutation = () =>
    useMutation({
      mutationKey: ['spare-part-replacement', minerId, 'undo-replacement'],
      mutationFn: (payload: UndoPartReplacmentPayload) => undoPartReplacement(minerId.value, payload),
      onError: (e: any) => {
        NotifyFail(e.message)
      },
      onSuccess: async () => {
        NotifySuccess('Part replacement rolled back to original.')
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['miner-details', minerId] }),
          queryClient.invalidateQueries({ queryKey: ['spare-parts'] }),
        ])
      },
    })

  const usePartDeleteMutation = () =>
    useMutation({
      mutationFn: (form: IMinerPartDeleteForm) => deleteMinerPart(minerId.value, form),
      onSuccess: async () => {
        NotifySuccess('Part deleted successfully')
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['miner-details', minerId] }),
          queryClient.invalidateQueries({ queryKey: ['spare-parts'] }),
        ])
      },
      onError: (e) => {
        NotifyFail(e.message)
      },
    })

  const usePartMoveToInventoryMutation = () =>
    useMutation({
      mutationFn: (form: IMinerPartMoveToInventoryForm) => movePartToInventory(minerId.value, form),
      onSuccess: async () => {
        NotifySuccess('Part moved to inventory successfully')
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['miner-details', minerId] }),
          queryClient.invalidateQueries({ queryKey: ['spare-parts'] }),
        ])
      },
      onError: (e) => {
        NotifyFail(e.message)
      },
    })

  const usePartMoveBackFromInventoryMutation = () =>
    useMutation({
      mutationFn: (form: IMinerPartMoveToInventoryForm) => movePartBackFromInventory(minerId.value, form),
      onSuccess: async () => {
        NotifySuccess('Part moved back from inventory successfully')
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['miner-details', minerId] }),
          queryClient.invalidateQueries({ queryKey: ['spare-parts'] }),
        ])
      },
      onError: (e) => {
        NotifyFail(e.message)
      },
    })

  const usePartAllocateMutation = () =>
    useMutation({
      mutationFn: (form: IMinerPartAllocationForm) => allocatePartToMiner(minerId.value, form),
      onSuccess: async () => {
        NotifySuccess('Part allocated successfully')
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['miner-details', minerId] }),
          queryClient.invalidateQueries({ queryKey: ['spare-parts'] }),
        ])
      },
      onError: (e) => {
        NotifyFail(e.message)
      },
    })

  return {
    usePartReplacementMutation,
    useUndoPartReplacementMutation,
    usePartDeleteMutation,
    usePartMoveToInventoryMutation,
    usePartMoveBackFromInventoryMutation,
    usePartAllocateMutation,
  }
}
